import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { DashboardLayout, WelcomeCard, RecentActivityCard } from './DashboardLayout';
import { dashboardService } from '@/services/dashboardService';
import type { AdminDashboardStats, DashboardActivity, SystemHealthStatus, UserStatistics } from '@/services/dashboardService';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/**
 * Admin Dashboard Component
 * 
 * Provides comprehensive overview for administrators including
 * system statistics, user management, and administrative controls.
 */
export function AdminDashboard() {
  useDocumentTitle('Admin Dashboard | SupportDesk');
  const { user } = useAuth();
  const [stats, setStats] = useState<any[]>([]);
  const [recentActivities, setRecentActivities] = useState<DashboardActivity[]>([]);
  const [systemHealth, setSystemHealth] = useState<SystemHealthStatus | null>(null);
  const [userStats, setUserStats] = useState<UserStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('Loading dashboard data for admin...');

      // Load all dashboard data in parallel
      const [statsResponse, activitiesResponse, healthResponse, userStatsResponse] = await Promise.all([
        dashboardService.getAdminStats(),
        dashboardService.getRecentActivities('Admin'),
        dashboardService.getSystemHealth(),
        dashboardService.getUserStatistics()
      ]);

      console.log('Dashboard responses received:', {
        stats: statsResponse.success,
        activities: activitiesResponse.success,
        health: healthResponse.success,
        userStats: userStatsResponse.success
      });

      // Process stats
      if (statsResponse.success && statsResponse.data) {
        const data = statsResponse.data;
        const formattedStats = [
          {
            label: 'Total Tickets',
            value: data.totalTickets.toLocaleString(),
            change: data.changes?.totalTickets,
            href: '/tickets',
            icon: (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            ),
          },
          {
            label: 'Active Agents',
            value: data.activeAgents.toString(),
            change: data.changes?.activeAgents,
            href: '/admin/users',
            icon: (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            ),
          },
          {
            label: 'Open Tickets',
            value: data.openTickets.toLocaleString(),
            change: data.changes?.openTickets,
            href: '/tickets?status=open',
            icon: (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
          },
          {
            label: 'Avg Response Time',
            value: data.avgResolutionTime,
            change: data.changes?.avgResolutionTime,
            href: '/reports/overview',
            icon: (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            ),
          },
        ];
        setStats(formattedStats);
      }

      // Process activities
      if (activitiesResponse.success) {
        setRecentActivities(activitiesResponse.data || []);
      }

      // Process system health
      if (healthResponse.success) {
        setSystemHealth(healthResponse.data || null);
      }

      // Process user statistics
      if (userStatsResponse.success) {
        setUserStats(userStatsResponse.data || null);
      }

    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to load dashboard data: ${errorMessage}. Please refresh the page or check your connection.`);
    } finally {
      setIsLoading(false);
    }
  };

  const actions = (
    <div className="flex space-x-3">
      <Link to="/admin/users">
        <Button variant="outline">
          Manage Users
        </Button>
      </Link>
      <Link to="/admin/settings">
        <Button variant="primary">
          System Settings
        </Button>
      </Link>
    </div>
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Operational':
        return 'bg-green-400';
      case 'Degraded':
        return 'bg-yellow-400';
      case 'Down':
        return 'bg-red-400';
      default:
        return 'bg-gray-400';
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  if (isLoading) {
    return (
      <DashboardLayout
        title="Admin Dashboard"
        subtitle="Manage your support desk system and monitor performance"
        stats={[]}
        actions={actions}
      >
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout
        title="Admin Dashboard"
        subtitle="Manage your support desk system and monitor performance"
        stats={[]}
        actions={actions}
      >
        <Card title="Error">
          <div className="p-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={loadDashboardData}>Retry</Button>
          </div>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Admin Dashboard"
      subtitle="Manage your support desk system and monitor performance"
      stats={stats}
      actions={actions}
    >
      {/* Welcome Section */}
      <WelcomeCard user={{ firstName: user.firstName, role: user.role }} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* System Health */}
        <Card title="System Health" data-testid="system-health-card">
          <div className="p-6">
            {systemHealth ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`h-3 w-3 rounded-full ${getStatusColor(systemHealth.apiService)}`}></div>
                    <span className="text-sm font-medium text-gray-900">API Service</span>
                  </div>
                  <span className="text-sm text-gray-500">{systemHealth.apiService}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`h-3 w-3 rounded-full ${getStatusColor(systemHealth.database)}`}></div>
                    <span className="text-sm font-medium text-gray-900">Database</span>
                  </div>
                  <span className="text-sm text-gray-500">{systemHealth.database}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`h-3 w-3 rounded-full ${getStatusColor(systemHealth.emailService)}`}></div>
                    <span className="text-sm font-medium text-gray-900">Email Service</span>
                  </div>
                  <span className="text-sm text-gray-500">{systemHealth.emailService}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`h-3 w-3 rounded-full ${getStatusColor(systemHealth.fileStorage)}`}></div>
                    <span className="text-sm font-medium text-gray-900">Local File System</span>
                  </div>
                  <span className="text-sm text-gray-500">{systemHealth.fileStorage}</span>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500">Loading system health...</div>
            )}
          </div>
        </Card>

        {/* Quick Actions */}
        <Card title="Quick Actions">
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <Link
                to="/admin/users?create=true"
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="text-center">
                  <div className="mx-auto h-8 w-8 text-primary-600 mb-2">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-900">Add User</span>
                </div>
              </Link>

              <Link
                to="/tickets"
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="text-center">
                  <div className="mx-auto h-8 w-8 text-primary-600 mb-2">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-900">View Tickets</span>
                </div>
              </Link>

              <Link
                to="/reports/overview"
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="text-center">
                  <div className="mx-auto h-8 w-8 text-primary-600 mb-2">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-900">Reports</span>
                </div>
              </Link>

              <Link
                to="/admin/settings"
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="text-center">
                  <div className="mx-auto h-8 w-8 text-primary-600 mb-2">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-900">Settings</span>
                </div>
              </Link>
            </div>
          </div>
        </Card>

        {/* Recent Activity */}
        <RecentActivityCard 
          activities={recentActivities}
          title="System Activity"
          emptyMessage="No recent system activity"
        />

        {/* User Statistics */}
        <Card title="User Statistics">
          <div className="p-6">
            {userStats ? (
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-900">Agents</span>
                    <span className="text-gray-500">{userStats.activeAgents} active</span>
                  </div>
                  <div className="mt-2 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full" 
                      style={{ width: userStats.totalAgents > 0 ? `${(userStats.activeAgents / userStats.totalAgents) * 100}%` : '0%' }}
                    ></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-900">End Users</span>
                    <span className="text-gray-500">{userStats.registeredEndUsers.toLocaleString()} registered</span>
                  </div>
                  <div className="mt-2 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full" 
                      style={{ width: userStats.totalUsers > 0 ? `${(userStats.registeredEndUsers / userStats.totalUsers) * 100}%` : '0%' }}
                    ></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-900">Signed in (last 24h)</span>
                    <span className="text-gray-500">{userStats.signedInLast24h.toLocaleString()} {userStats.signedInLast24h === 1 ? 'user' : 'users'}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500">{isLoading ? 'Loading user statistics...' : 'User statistics are unavailable.'}</div>
            )}

            <div className="mt-6 pt-6 border-t border-gray-200">
              <Link
                to="/admin/users"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                View all users →
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
